import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, mapTo, retry } from 'rxjs/operators';
import { AutosaveFormDetailsParams, AutoSaveOwnersDetailsStatus, OwnerInformationFormData, OwnersDiffData } from '../../form-details.model';
import { SubmissionsFacade } from '../../../../../../domain/+state/submissions.facade';

@Injectable({
  providedIn: 'root'
})
export class AutosaveOwnerInfoStepService {
  constructor(
    private readonly submissionsFacade: SubmissionsFacade) { }

  saveOwnersInformationStep(ownersDiffData: OwnersDiffData, params: AutosaveFormDetailsParams, forceCreation?: boolean): Observable<AutoSaveOwnersDetailsStatus> {
    const addedOrUpdated: OwnerInformationFormData[] = ownersDiffData?.addedOrUpdated ?? [];
    const removed: OwnerInformationFormData[] = ownersDiffData?.removed ?? [];

    if (addedOrUpdated.length === 0 && removed.length === 0) {
      return of({ addedOrUpdated: [], removed: [] });
    }

    const addOrUpdateObservables: Observable<OwnerInformationFormData>[] = addedOrUpdated.map(owner => {
      if (forceCreation || !owner?.id) {
        return this.createNewContact(params, owner);
      }
      return this.updateContact(owner);
    });

    const removeObservables: Observable<OwnerInformationFormData>[] =
      removed.map(owner => this.removeContact(owner));

    const addedOrUpdated$ = addOrUpdateObservables.length > 0 ? forkJoin(addOrUpdateObservables) : of([]);
    const removed$ = removeObservables.length > 0 ? forkJoin(removeObservables) : of([]);

    return forkJoin({ addedOrUpdated: addedOrUpdated$, removed: removed$ });
  }

  private createNewContact(params: AutosaveFormDetailsParams, owner: OwnerInformationFormData): Observable<OwnerInformationFormData> {
    return this.submissionsFacade.createContactData({
      ...owner,
      brokerId: params.brokerId,
      accountId: params.accountId,
      applicationId: params.applicationId,
      active: true,
    }, true).pipe(retry(3), map((data) => ({
      ...owner,
      id: <number>data.id,
      createdOn: data.createdOn ? new Date(data.createdOn) : undefined })));
  }

  private updateContact(owner: OwnerInformationFormData): Observable<OwnerInformationFormData> {
    return this.submissionsFacade.updateContactData(owner.id, owner, true).pipe(retry(3), mapTo(owner));
  }

  private removeContact(owner: OwnerInformationFormData): Observable<OwnerInformationFormData> {
    if (!owner?.id) {
      console.error("Cannot remove owner, no id provided");
      return of();
    }

    return this.submissionsFacade.removeContactData(owner.id, true).pipe(retry(3), mapTo(owner));
  }
}
